/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Helpers from '@Apis/helpers';
import dayjs from 'dayjs';
import AnalyticsActions from '@Actions/application/analyticsActions';
import DailyRunRateActions from '@Actions/inventory/dailyRunRateActions';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import MetricInvExpirySplit from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricInvExpirySplit';
import MetricOpenStockTransfers from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricOpenStockTransfers';
import MetricPinnedDailyRunRates from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricPinnedDailyRunRates';
import MetricActiveProducts from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricActiveProducts';
import MetricInvExpirySplitCategory
from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricInvExpirySplitCategory';
import MetricInvAgeingSplit from '@Components/Home/HomeTabs/InventoryDashboard/Widget/MetricInvAgeingSplit';
import { entityNameEnum, filterTypeEnums } from '../../../../apis/constants';
import useCheckPortrait from '../../../../hooks/useCheckPortrait';
import UserSavedFilterViewSelector from '../../../Common/UserSavedFilterViewSelector';
import './style.scss';

function InventoryDashboard({
  user, history, getAnalyticsData, INVENTORY_STOCK_VALUATION, INVENTORY_OPEN_TRANSFERS, INVENTORY_STOCK_STATUS_SPLIT, INVENTORY_AGEING_SPLIT, getDailyRunRates, dailyRunRates, getDailyRunRatesLoading,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState([]);
  const [isUserReady, setIsUserReady] = useState(false);
  const [call, setCall] = useState(false);

  const [currentDefaultView, setCurrentDefaultView] = useState({});
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState([]);
  const [selectedFilterView, setSelectedFilterView] = useState({});
  const [isUserReadyInitial, setIsUserReadyInitial] = useState(false);
  const [isUpdateFilterSate, setIsUpdateFilterSate] = useState(true);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [savedFilterViewsLoading, setSavedFilterViewsLoading] = useState(false);

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  function getFilterValueByKey({ filtersArray, key, valueOnly = true }) {
    const selectedFilter = filtersArray.find((item) => item.filter_key === key) || null;
    return valueOnly ? selectedFilter?.filter_value : selectedFilter;
  }

  function getAppliedFiltersFromURL() {
    const appliedFiltersFromURL = [
      {
        filter_key: 'tenant',
        filter_value: Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : [],
        filter_type: filterTypeEnums.ARRAY,
      },
    ];
    return appliedFiltersFromURL;
  }

  const initialFilterValues = useMemo(() => [
    {
      filter_key: 'tenant',
      filter_value: Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ),
      filter_type: filterTypeEnums.ARRAY,
    },
  ], []);

  const systemDefaultUserView = useMemo(() => ({
    filter_id: 'system_default',
    filter_name: 'System Default View',
    entity_name: entityNameEnum.INVENTORY_DASHBOARD,
    filters: initialFilterValues,
    tenant_id: user?.tenant_info?.tenant_id,
    org_id: user?.user_org_id,
    user_id: user?.user_id,
    is_default: false,
    is_system_default: true,
  }), []);

  const pushParamsToUrl = (tenant, selectedFilterView) => {
    let url = '?tab=/inventory-dashboard&';
    if (tenant) url += `tenant=${tenant}`;
    if (selectedFilterView) url += `&filter_id=${selectedFilterView?.filter_id}`;
    history.push(url);
  };

  useEffect(() => {
    if (user?.tenant_info?.org_id) {
      // setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
      // setSelectedTenant(Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ));
      // setIsUserReady(true);
      setSelectedFilterView(systemDefaultUserView);
    }
  }, []);

  useEffect(() => {
    if (savedFilterViews && isUserReadyInitial) {
      const defaultUserView = savedFilterViews.find((item) => item.is_default);

      if (defaultUserView) {
        setCurrentDefaultView(defaultUserView);
      } else {
        setCurrentDefaultView(systemDefaultUserView);
      }

      const appliedFiltersFromURL = getAppliedFiltersFromURL();

      const selectedFilterViewFromURL = Helpers.getValueFromUrl('filter_id');

      let currentSelectedView;
      if (selectedFilterViewFromURL) {
        currentSelectedView = savedFilterViews.find((item) => String(item.filter_id) === String(selectedFilterViewFromURL));
        if (!currentSelectedView) {
          currentSelectedView = systemDefaultUserView;
        }
      } else {
        currentSelectedView = defaultUserView || systemDefaultUserView;
      }

      const filterValues = (isFromURL) => {
        const currentSelectedViewFilters = currentSelectedView.filters;

        const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: isFromURL ? appliedFiltersFromURL : currentSelectedViewFilters, key: filterKey }) }), {});

        return filterValue;
      };

      const tempFilterValues = filterValues(selectedFilterViewFromURL);

      setSelectedFilterView(currentSelectedView);
      if (isUpdateFilterSate) {
        setCurrentlyAppliedFilters(appliedFiltersFromURL);

        setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
        setSelectedTenant(tempFilterValues.tenant);
      }

      if (!isUserReady) setIsUserReady(true);
      if (!isUpdateFilterSate) setIsUpdateFilterSate(true);
    }
  }, [savedFilterViews, isUserReadyInitial]);

  useEffect(() => {
    if (isUserReady) {
      pushParamsToUrl(selectedTenant, selectedFilterView);
      setCurrentlyAppliedFilters(getAppliedFiltersFromURL());
      getAnalyticsData('INVENTORY', 'STOCK_STATUS_SPLIT', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(','),
      });
      getAnalyticsData('INVENTORY', 'STOCK_VALUATION', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(','),
      });
      getAnalyticsData('INVENTORY', 'OPEN_TRANSFERS', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(','),
      });
      getAnalyticsData('INVENTORY', 'AGEING_SPLIT', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(','),
        interval: 30,
        steps: 6,
      });
      const payload = {
        tenantId: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)?.join(','),
        page: Helpers.getValueFromUrl('page'),
        limit: Helpers.getValueFromUrl('limit'),
      };
      getDailyRunRates(payload);
    }
  }, [selectedTenant, call]);

  const getDailyRunRateData = () => {
    return dailyRunRates?.data?.daily_run_rates?.filter((item) => item?.is_pinned)?.map((item) => {
      const outwardTenantStocksUnits = item?.outward_tenant_skus;
      const inStock = !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
        ? 0
        : outwardTenantStocksUnits?.reduce((acc, curr) => {
          return curr?.status === 'In Stock' ? acc + 1 : acc;
        }, 0);
      const runningLow = !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
        ? 0
        : outwardTenantStocksUnits?.reduce((acc, curr) => {
          return curr?.status === 'Running Low' ? acc + 1 : acc;
        }, 0);
      const outOfStock =
        !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
          ? 0
          : outwardTenantStocksUnits?.reduce((acc, curr) => {
            return curr?.status === 'Out of Stock' ? acc + 1 : acc;
          }, 0);
      return {
        drr_id: item?.drr_id,
        tenant_name: item?.tenant_info?.tenant_name,
        tenant_id: item?.tenant_id,
        list_name: item?.sku_list_info?.list_name,
        inStock,
        runningLow,
        outOfStock,
      };
    });
  };

  return (
    <Fragment>
      <div className="dashboard-filters doc-list__wrapper doc-list__tab-content">
        <div className="flex-display" style={{ gap: '10px' }}>
          <UserSavedFilterViewSelector
            selectedFilterView={selectedFilterView}
            onChangeTab={(currentSelectedView) => {
              setSelectedFilterView(() => {
                const currentSelectedViewFilters = currentSelectedView.filters;

                const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: currentSelectedViewFilters, key: filterKey }) }), {});

                const tempFilterValues = filterValue;
                setCurrentPage(1);

                setSelectedTenant(tempFilterValues.tenant);

                return currentSelectedView;
              });
            }}
            currentlyAppliedFilters={currentlyAppliedFilters}
            entityName={entityNameEnum.INVENTORY_DASHBOARD}
            setSavedFilterViews={(value) => setSavedFilterViews(value)}
            setSavedFilterViewsLoading={(value) => setSavedFilterViewsLoading(value)}
            setIsUpdateFilterSate={(value) => setIsUpdateFilterSate(value)}
            callback={() => {
              if (!isUserReadyInitial) setIsUserReadyInitial(true);
            }}
            systemDefaultUserView={systemDefaultUserView}
          />
          <TenantSelector
            selectedTenant={selectedTenant}
            showSearch
            showAll={false}
            noDropdownAlign
            isMultiple
            onChange={(value) => {
              setSelectedTenant(value);
            }}
            placeholder="Select Business Unit"
            inputClassName="orgInputContainer"
            hideTitle
            includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)}
          />
        </div>
      </div>
      <div className="ant-row" style={{ marginTop: '20px', paddingBottom: '10px' }}>
        <div className="ant-col-md-19">
          <div className="ant-row">
            <div className="ant-col-md-10 metric-mobile-view">
              <MetricInvAgeingSplit
                data={INVENTORY_AGEING_SPLIT?.data?.[0]}
                loading={INVENTORY_AGEING_SPLIT?.loading}
                tenantIds={selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(',')}
              />
            </div>
            <div className="ant-col-md-14 metric-mobile-view">
              <MetricInvExpirySplit
                data={INVENTORY_STOCK_VALUATION?.data?.[0]}
                loading={INVENTORY_STOCK_VALUATION?.loading}
                tenantIds={selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(',')}
              />
            </div>
            <div className="ant-col-md-10 metric-mobile-view">
              <MetricOpenStockTransfers
                data={INVENTORY_OPEN_TRANSFERS?.data}
                loading={INVENTORY_OPEN_TRANSFERS?.loading}
                tenantIds={selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ)?.join(',')}
              />
            </div>
            <div className="ant-col-md-14 metric-mobile-view">
              <MetricPinnedDailyRunRates
                data={getDailyRunRateData()}
                loading={getDailyRunRatesLoading}
                tenantIds={selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)?.join(',')}
              />
            </div>
          </div>
        </div>
        <div className="ant-col-md-5">
          <MetricInvExpirySplitCategory
            data={INVENTORY_STOCK_VALUATION?.data?.[0]}
            loading={INVENTORY_STOCK_VALUATION?.loading}
            tenantIds={selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)?.join(',')}
          />
        </div>
      </div>
    </Fragment>
  );
}

const mapStateToProps = ({ UserReducers, AnalyticsReducers, DailyRunRateReducers }) => ({
  user: UserReducers.user,
  analytics: AnalyticsReducers.analytics,
  INVENTORY_STOCK_VALUATION: AnalyticsReducers.INVENTORY_STOCK_VALUATION,
  INVENTORY_OPEN_TRANSFERS: AnalyticsReducers.INVENTORY_OPEN_TRANSFERS,
  INVENTORY_STOCK_STATUS_SPLIT: AnalyticsReducers.INVENTORY_STOCK_STATUS_SPLIT,
  INVENTORY_AGEING_SPLIT: AnalyticsReducers.INVENTORY_AGEING_SPLIT,
  getDailyRunRatesLoading: DailyRunRateReducers.getDailyRunRatesLoading,
  dailyRunRates: DailyRunRateReducers.dailyRunRates,
});

const mapDispatchToProps = (dispatch) => ({
  getAnalyticsData: (dataType, name, params) => dispatch(AnalyticsActions.getAnalyticsData(dataType, name, params)),
  getDailyRunRates: (payload, callback) => dispatch(DailyRunRateActions.getDailyRunRates(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(InventoryDashboard));
